
import React, { useState } from 'react';

const ConsultationForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessInterest: '',
    budget: '',
    timeline: '',
    experience: '',
    location: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4ck1chpkd5ovn9u5t70', {
        method: 'POST',
        body: formDataToSubmit,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          businessInterest: '',
          budget: '',
          timeline: '',
          experience: '',
          location: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Book Your Free Consultation
      </h2>
      
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          <div className="flex items-center">
            <i className="ri-check-circle-line text-xl mr-2"></i>
            <span>Thank you! Your consultation request has been submitted successfully. We'll contact you within 24 hours.</span>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <div className="flex items-center">
            <i className="ri-error-warning-line text-xl mr-2"></i>
            <span>Sorry, there was an error submitting your request. Please try again or contact us directly.</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} id="consultation-form" data-readdy-form className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm"
              placeholder="Enter your first name"
            />
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm"
              placeholder="Enter your last name"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm"
            placeholder="Enter your email address"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm"
            placeholder="Enter your phone number"
          />
        </div>

        <div>
          <label htmlFor="businessInterest" className="block text-sm font-medium text-gray-700 mb-2">
            Type of Business Interest *
          </label>
          <div className="relative">
            <select
              id="businessInterest"
              name="businessInterest"
              value={formData.businessInterest}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm pr-8 appearance-none cursor-pointer"
            >
              <option value="">Select business type</option>
              <option value="restaurant">Restaurant/Food Service</option>
              <option value="retail">Retail Store</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="service">Service Business</option>
              <option value="healthcare">Healthcare Services</option>
              <option value="automotive">Automotive</option>
              <option value="technology">Technology/Software</option>
              <option value="construction">Construction</option>
              <option value="ecommerce">E-commerce</option>
              <option value="other">Other</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-3 text-gray-400 pointer-events-none"></i>
          </div>
        </div>

        <div>
          <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
            Investment Budget *
          </label>
          <div className="relative">
            <select
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm pr-8 appearance-none cursor-pointer"
            >
              <option value="">Select budget range</option>
              <option value="under-100k">Under $100,000</option>
              <option value="100k-250k">$100,000 - $250,000</option>
              <option value="250k-500k">$250,000 - $500,000</option>
              <option value="500k-1m">$500,000 - $1,000,000</option>
              <option value="1m-2m">$1,000,000 - $2,000,000</option>
              <option value="over-2m">Over $2,000,000</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-3 text-gray-400 pointer-events-none"></i>
          </div>
        </div>

        <div>
          <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Timeline *
          </label>
          <div className="relative">
            <select
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm pr-8 appearance-none cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="immediately">Immediately (0-3 months)</option>
              <option value="short-term">Short term (3-6 months)</option>
              <option value="medium-term">Medium term (6-12 months)</option>
              <option value="long-term">Long term (12+ months)</option>
              <option value="exploring">Just exploring</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-3 text-gray-400 pointer-events-none"></i>
          </div>
        </div>

        <div>
          <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
            Business Experience *
          </label>
          <div className="relative">
            <select
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm pr-8 appearance-none cursor-pointer"
            >
              <option value="">Select experience level</option>
              <option value="first-time">First-time buyer</option>
              <option value="some-experience">Some business experience</option>
              <option value="experienced">Experienced business owner</option>
              <option value="serial-entrepreneur">Serial entrepreneur</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-3 text-gray-400 pointer-events-none"></i>
          </div>
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Location
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm"
            placeholder="City, State or Region"
          />
        </div>

        <div>
          <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Information
          </label>
          <textarea
            id="additionalInfo"
            name="additionalInfo"
            value={formData.additionalInfo}
            onChange={handleInputChange}
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-sm resize-vertical"
            placeholder="Tell us more about your business acquisition goals..."
          ></textarea>
          <div className="text-right text-sm text-gray-500 mt-1">
            {formData.additionalInfo.length}/500 characters
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-gray-400 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <i className="ri-send-plane-line mr-2"></i>
              Schedule My Free Consultation
            </div>
          )}
        </button>
      </form>
    </div>
  );
};

export default ConsultationForm;
